import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/services/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { patchApplicationsCache } from '@/lib/recruiterCache';
import type { Application, RecruiterNote, RecruiterNoteVisibility } from '@/types';

function splitNotes(notes: RecruiterNote[]) {
  const ownPrivate = notes.find((n) => n.isOwn && n.visibility === 'private');
  const ownTeam = notes.find((n) => n.isOwn && n.visibility === 'public');
  const otherTeam = notes.filter((n) => n.visibility === 'public' && !n.isOwn);
  return { ownPrivate, ownTeam, otherTeam };
}

export function useApplicationNotes(
  companyId: string | undefined,
  app: Application | null,
  userId: string | undefined,
) {
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [notes, setNotes] = useState<RecruiterNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState<RecruiterNoteVisibility | null>(null);

  const [editingPrivate, setEditingPrivate] = useState(false);
  const [editingTeam, setEditingTeam] = useState(false);
  const editingPrivateRef = useRef(false);
  const editingTeamRef = useRef(false);
  editingPrivateRef.current = editingPrivate;
  editingTeamRef.current = editingTeam;
  const [privateDraft, setPrivateDraft] = useState('');
  const [teamDraft, setTeamDraft] = useState('');

  const { ownPrivate, ownTeam, otherTeam } = splitNotes(notes);

  const loadNotes = useCallback(async () => {
    if (!companyId || !app || !userId) {
      setNotes([]);
      return;
    }
    setLoading(true);
    try {
      const loaded = await api.applications.listNotes(companyId, app.positionId, app.id);
      setNotes(loaded);
      const split = splitNotes(loaded);
      if (!editingPrivateRef.current) setPrivateDraft(split.ownPrivate?.body ?? '');
      if (!editingTeamRef.current) setTeamDraft(split.ownTeam?.body ?? '');
    } catch {
      setNotes([]);
      toast.error(t('appDetail.notesLoadError'));
    } finally {
      setLoading(false);
    }
  }, [companyId, app, userId]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  const saveNote = useCallback(async (visibility: RecruiterNoteVisibility) => {
    if (!companyId || !app || !userId || savingVisibility) return;
    const draft = visibility === 'private' ? privateDraft : teamDraft;
    const trimmed = draft.trim();
    if (!trimmed) return;

    setSavingVisibility(visibility);
    try {
      const saved = await api.applications.upsertNote(
        companyId,
        app.positionId,
        app.id,
        { body: trimmed, visibility },
      );

      const reloaded = await api.applications.listNotes(companyId, app.positionId, app.id);
      setNotes(reloaded);

      if (visibility === 'private') {
        setEditingPrivate(false);
        setPrivateDraft(saved.body);
      } else {
        setEditingTeam(false);
        setTeamDraft(saved.body);
      }

      const hasPublicNotes = reloaded.some((note) => note.visibility === 'public');
      patchApplicationsCache(queryClient, companyId, [{
        ...app,
        hasPublicNotes,
      }]);
      toast.success(t('appDetail.notesSaveSuccess'));
    } catch {
      toast.error(t('appDetail.notesSaveError'));
    } finally {
      setSavingVisibility(null);
    }
  }, [
    companyId,
    app,
    userId,
    privateDraft,
    teamDraft,
    savingVisibility,
    queryClient,
    t,
  ]);

  const startEditPrivate = useCallback(() => {
    setPrivateDraft(ownPrivate?.body ?? '');
    setEditingPrivate(true);
  }, [ownPrivate?.body]);

  const startEditTeam = useCallback(() => {
    setTeamDraft(ownTeam?.body ?? '');
    setEditingTeam(true);
  }, [ownTeam?.body]);

  const cancelEditPrivate = useCallback(() => {
    setPrivateDraft(ownPrivate?.body ?? '');
    setEditingPrivate(false);
  }, [ownPrivate?.body]);

  const cancelEditTeam = useCallback(() => {
    setTeamDraft(ownTeam?.body ?? '');
    setEditingTeam(false);
  }, [ownTeam?.body]);

  return {
    notes,
    ownPrivateNote: ownPrivate,
    ownTeamNote: ownTeam,
    otherTeamNotes: otherTeam,
    privateDraft,
    setPrivateDraft,
    teamDraft,
    setTeamDraft,
    editingPrivate,
    editingTeam,
    startEditPrivate,
    startEditTeam,
    cancelEditPrivate,
    cancelEditTeam,
    loading,
    savingPrivate: savingVisibility === 'private',
    savingTeam: savingVisibility === 'public',
    savePrivateNote: () => saveNote('private'),
    saveTeamNote: () => saveNote('public'),
  };
}
